class AddToCartRecommendationPopup extends HTMLElement {
  connectedCallback() {
    this.closeButton = this.querySelector('[data-popup-close]');
    this.overlay = this.querySelector('[data-popup-overlay]');
    this.errorElement = this.querySelector('[data-popup-error]');
    this.successElement = this.querySelector('[data-popup-success]');
    this.products = this.querySelector('[data-popup-products]');
    this.pagination = this.querySelector('[data-popup-pagination]');
    this.productCards = Array.from(this.products?.querySelectorAll('.add-to-cart-recommendation-popup__product') || []);
    this.recommendedProductIds = new Set(
      this.productCards.map((card) => String(card.dataset.recommendationProductId)).filter(Boolean)
    );
    this.productPages = [];
    this.recommendationButtons = this.querySelectorAll
      ? this.querySelectorAll('[data-recommendation-add]')
      : [this.querySelector('[data-recommendation-add]')].filter(Boolean);

    this.handleProductAdded = this.handleProductAdded.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.updatePagination = this.updatePagination.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.preventCardNativeDrag = this.preventCardNativeDrag.bind(this);
    this.close = this.close.bind(this);

    document.addEventListener('product-ajax:added', this.handleProductAdded);
    document.addEventListener('keydown', this.handleKeydown);
    this.closeButton?.addEventListener('click', this.close);
    this.overlay?.addEventListener('click', this.close);
    this.addEventListener('click', this.handleBackdropClick);
    this.recommendationButtons.forEach((button) => button.addEventListener('click', (event) => this.addRecommendation(event)));
    this.setupPagination();
  }

  disconnectedCallback() {
    document.removeEventListener('product-ajax:added', this.handleProductAdded);
    document.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('click', this.handleBackdropClick);
    this.products?.removeEventListener('scroll', this.updatePagination);
    this.products?.removeEventListener('pointerdown', this.handlePointerDown);
    this.products?.removeEventListener('pointermove', this.handlePointerMove);
    this.products?.removeEventListener('pointerup', this.handlePointerUp);
    this.products?.removeEventListener('pointercancel', this.handlePointerUp);
    this.products?.removeEventListener('touchstart', this.handleTouchStart);
    this.products?.removeEventListener('touchmove', this.handleTouchMove);
    this.products?.removeEventListener('touchend', this.handleTouchEnd);
    this.products?.removeEventListener('touchcancel', this.handleTouchEnd);
    this.products?.removeEventListener('dragstart', this.preventCardNativeDrag);
    this.products?.removeEventListener('selectstart', this.preventCardNativeDrag);
  }

  setupPagination() {
    if (!this.products || !this.pagination) return;

    this.groupProductCards();
    const pageCount = this.productPages.length;
    this.pageCount = pageCount;
    this.pagination.replaceChildren();
    this.pagination.hidden = pageCount <= 1;
    if (pageCount <= 1) return;

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'add-to-cart-recommendation-popup__page';
      button.setAttribute('data-popup-page', pageIndex);
      button.setAttribute('aria-label', `Go to product page ${pageIndex + 1}`);
      button.addEventListener('click', () => this.goToPage(pageIndex));
      this.pagination.appendChild(button);
    }

    this.products.addEventListener('scroll', this.updatePagination, { passive: true });
    this.products.addEventListener('pointerdown', this.handlePointerDown);
    this.products.addEventListener('pointermove', this.handlePointerMove);
    this.products.addEventListener('pointerup', this.handlePointerUp);
    this.products.addEventListener('pointercancel', this.handlePointerUp);
    this.products.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.products.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.products.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    this.products.addEventListener('touchcancel', this.handleTouchEnd, { passive: true });
    this.products.addEventListener('dragstart', this.preventCardNativeDrag);
    this.products.addEventListener('selectstart', this.preventCardNativeDrag);
    this.setActivePage(0);
  }

  getPageSize() {
    return window.matchMedia('(max-width: 749px)').matches ? 2 : 3;
  }

  groupProductCards() {
    const pageSize = this.getPageSize();
    const fragment = document.createDocumentFragment();

    this.productCards.forEach((card, index) => {
      if (index % pageSize === 0) {
        const page = document.createElement('div');
        page.className = 'add-to-cart-recommendation-popup__product-page';
        page.dataset.productPage = '';
        fragment.appendChild(page);
      }
      fragment.lastChild.appendChild(card);
    });

    this.products.replaceChildren(fragment);
    this.productPages = Array.from(this.products.querySelectorAll('[data-product-page]'));
    this.products.classList.toggle('is-paginated', this.productPages.length > 1);
  }

  handlePointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartY = Number.isFinite(event.clientY) ? event.clientY : 0;
    this.dragStartScrollLeft = this.products.scrollLeft;
    this.dragStartPage = this.getClosestPageIndex();
    this.dragAxis = null;
  }

  handlePointerMove(event) {
    if (event.pointerId !== this.dragPointerId) return;
    const distanceX = this.dragStartX - event.clientX;
    const distanceY = this.dragStartY - (Number.isFinite(event.clientY) ? event.clientY : 0);
    if (!this.dragAxis) {
      if (Math.max(Math.abs(distanceX), Math.abs(distanceY)) < 3) return;
      if (Math.abs(distanceY) > Math.abs(distanceX)) {
        this.finishPointerDrag(event, false);
        return;
      }
      this.dragAxis = 'x';
      this.products.setPointerCapture?.(event.pointerId);
      this.products.classList.add('is-dragging');
    }
    event.preventDefault();
    this.products.scrollLeft = this.dragStartScrollLeft + distanceX;
    this.updatePagination();
  }

  handlePointerUp(event) {
    if (event.pointerId !== this.dragPointerId) return;
    const distanceX = this.dragStartX - event.clientX;
    const shouldSnap = event.type !== 'pointercancel' && this.dragAxis === 'x';
    let targetPage = this.getClosestPageIndex();
    if (shouldSnap && Math.abs(distanceX) >= 40) {
      targetPage = this.dragStartPage + (distanceX > 0 ? 1 : -1);
    }
    this.finishPointerDrag(event, shouldSnap, targetPage);
  }

  handleTouchStart(event) {
    const touch = event.touches[0];
    if (!touch) return;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartScrollLeft = this.products.scrollLeft;
    this.touchStartPage = this.getClosestPageIndex();
    this.touchAxis = null;
  }

  handleTouchMove(event) {
    const touch = event.touches[0];
    if (!touch || this.touchStartX == null) return;
    const distanceX = this.touchStartX - touch.clientX;
    const distanceY = this.touchStartY - touch.clientY;
    if (!this.touchAxis) {
      if (Math.max(Math.abs(distanceX), Math.abs(distanceY)) < 3) return;
      if (Math.abs(distanceY) > Math.abs(distanceX)) {
        this.resetTouchDrag();
        return;
      }
      this.touchAxis = 'x';
      this.products.classList.add('is-dragging');
    }
    event.preventDefault();
    this.products.scrollLeft = this.touchStartScrollLeft + distanceX;
    this.updatePagination();
  }

  handleTouchEnd(event) {
    if (this.touchStartX == null) return;
    const touch = event.changedTouches[0];
    const distanceX = touch ? this.touchStartX - touch.clientX : 0;
    const shouldSnap = event.type !== 'touchcancel' && this.touchAxis === 'x';
    let targetPage = this.getClosestPageIndex();
    if (shouldSnap && Math.abs(distanceX) >= 40) {
      targetPage = this.touchStartPage + (distanceX > 0 ? 1 : -1);
    }
    this.resetTouchDrag();
    if (shouldSnap) this.goToPage(this.clampPageIndex(targetPage));
  }

  preventCardNativeDrag(event) {
    event.preventDefault();
  }

  resetTouchDrag() {
    this.products.classList.remove('is-dragging');
    this.touchStartX = null;
    this.touchStartY = null;
    this.touchAxis = null;
  }

  finishPointerDrag(event, shouldSnap, targetPage = 0) {
    this.products.releasePointerCapture?.(event.pointerId);
    this.products.classList.remove('is-dragging');
    this.dragPointerId = null;
    this.dragAxis = null;
    if (shouldSnap) this.goToPage(this.clampPageIndex(targetPage));
  }

  clampPageIndex(pageIndex) {
    return Math.max(0, Math.min(pageIndex, (this.pageCount || 1) - 1));
  }

  updatePagination() {
    if (!this.products?.clientWidth) return;
    this.setActivePage(this.getClosestPageIndex());
  }

  getClosestPageIndex() {
    const pages = this.pagination?.querySelectorAll('[data-popup-page]') || [];
    let activePage = 0;
    let closestDistance = Infinity;
    pages.forEach((button, pageIndex) => {
      const distance = Math.abs(this.products.scrollLeft - this.getPageOffset(pageIndex));
      if (distance < closestDistance) {
        activePage = pageIndex;
        closestDistance = distance;
      }
    });
    return activePage;
  }

  setActivePage(pageIndex) {
    this.pagination?.querySelectorAll('[data-popup-page]').forEach((button, index) => {
      if (index === pageIndex) button.setAttribute('aria-current', 'page');
      else button.removeAttribute('aria-current');
    });
  }

  goToPage(pageIndex) {
    if (!this.products) return;
    this.products.scrollTo({ left: this.getPageOffset(pageIndex), behavior: 'smooth' });
    this.setActivePage(pageIndex);
  }

  getPageOffset(pageIndex) {
    const firstPage = this.productPages[0];
    const targetPage = this.productPages[pageIndex];
    if (Number.isFinite(firstPage?.offsetLeft) && Number.isFinite(targetPage?.offsetLeft)) {
      return targetPage.offsetLeft - firstPage.offsetLeft;
    }

    const firstCard = this.productCards[0];
    const pageCard = this.productCards[pageIndex * 2];
    if (Number.isFinite(firstCard?.offsetLeft) && Number.isFinite(pageCard?.offsetLeft)) {
      return pageCard.offsetLeft - firstCard.offsetLeft;
    }
    return pageIndex * (this.products?.clientWidth || 0);
  }

  handleProductAdded() {
    window.clearTimeout(this.openTimer);
    this.openTimer = window.setTimeout(() => this.openIfEligible(), 0);
  }

  async openIfEligible() {
    if (await this.shouldSuppressPopupForCart()) return;
    this.open();
  }

  async shouldSuppressPopupForCart() {
    if (!this.recommendedProductIds.size) return false;

    try {
      const cart = await this.fetchCart();
      return cart.items?.some((item) => this.recommendedProductIds.has(String(item.product_id))) || false;
    } catch (error) {
      return false;
    }
  }

  async parseJsonResponse(response, fallbackMessage) {
    try {
      return await response.json();
    } catch (error) {
      throw new Error(fallbackMessage);
    }
  }

  async fetchCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    const cart = await this.parseJsonResponse(response, 'Unable to refresh your bag.');
    if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to refresh your bag.');
    return cart;
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && !this.hidden) this.close();
  }

  handleBackdropClick(event) {
    if (event.target === this || event.target === this.overlay) this.close(event);
  }

  open() {
    this.hidden = false;
    this.setAttribute('aria-hidden', 'false');
    this.closeButton?.focus();
  }

  close(event) {
    event?.preventDefault();
    this.hidden = true;
    this.setAttribute('aria-hidden', 'true');
  }

  async addRecommendation(event) {
    event.preventDefault();
    const button = event.currentTarget || event.target;
    const variantId = button?.dataset.variantId;
    if (!variantId || button.hasAttribute('disabled')) return;

    button.setAttribute('disabled', 'disabled');
    button.classList.add('is-loading');
    this.showError('');

    try {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const result = await this.parseJsonResponse(response, 'Unable to add this item.');
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this item.');

      const cart = await this.fetchCart();
      cart.sections = result.sections;
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
      this.showSuccess('Added to bag!');
    } catch (error) {
      this.showError(error.message || 'Unable to add this item.');
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('disabled');
    }
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = !message;
  }

  showSuccess(message) {
    if (!this.successElement) return;
    window.clearTimeout(this.successTimer);
    this.successElement.textContent = message;
    this.successElement.hidden = false;
    this.successTimer = window.setTimeout(() => {
      this.successElement.hidden = true;
    }, 2500);
  }
}

customElements.define('add-to-cart-recommendation-popup', AddToCartRecommendationPopup);
